import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import { ReactComponent as ErrorIcon } from "assets/icons/error.svg";
import { ReactComponent as InfoIcon } from "assets/icons/info.svg";
import { ReactComponent as SuccessIcon } from "assets/icons/success.svg";
import { ReactComponent as WarningIcon } from "assets/icons/warning.svg";
import { toastTime } from "consts";
import { memo, useEffect, useRef } from "react";
import {
  CloseButton,
  Container,
  CustomButton,
  IconContent,
  InfoContent,
  TitleContent,
  ToastOptionTypes,
  ToastStyleType,
} from "./styles";

export type ToastType = ToastStyleType & {
  title?: string;
  description: string;
  action?: { text: string; onClick: () => void };
  fixed?: boolean;
  removeMe?: () => void;
};

const getTypeIcon = (type: ToastOptionTypes) => {
  switch (type) {
    case "error":
      return <ErrorIcon />;
    case "warning":
      return <WarningIcon />;
    case "info":
      return <InfoIcon />;
    default:
      return <SuccessIcon />;
  }
};

const Toast = ({
  type = "success",
  title,
  description,
  action,
  fixed,
  removeMe,
  ...args
}: ToastType) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    const currentRef = ref?.current;

    if (fixed) return;

    const callAsyncFunction = async () => {
      await new Promise(() => {
        timer = setTimeout(() => removeMe?.(), toastTime);
        currentRef?.addEventListener("mouseenter", () => {
          clearTimeout(timer);
        });
        currentRef?.addEventListener("mouseleave", () => {
          timer = setTimeout(() => removeMe?.(), toastTime / 2);
        });
      });
    };

    callAsyncFunction();

    return () => {
      clearTimeout(timer);
    };
  }, [fixed, removeMe]);

  return (
    <Container {...args} type={type} ref={ref}>
      <IconContent type={type}>{getTypeIcon(type)}</IconContent>
      <InfoContent>
        <TitleContent>
          <p>
            <b>{title}</b>
          </p>
          <CloseButton icon={<CloseIcon />} onClick={removeMe} />
        </TitleContent>
        <p title={description}>{description}</p>
        {action && (
          <CustomButton onClick={action.onClick}>{action.text}</CustomButton>
        )}
      </InfoContent>
    </Container>
  );
};

export default memo(Toast);

export type { ToastOptionTypes };
